import React, { useState } from "react";
import { useAuth } from "../context/AuthContext";
import { useAccess } from "../context/AccessContext";
import { useTheme } from "../context/ThemeContext";
import { usePremiumStatus, formatExpiryDate } from "../hooks/usePremiumStatus";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "../components/ui/alert-dialog";
import { useNavigate } from "react-router-dom";
import api, { clearToken } from "../lib/api";
import { LogOut, Sun, Moon, Monitor, Crown, Lock, Check, AlertTriangle, Trash2, ShieldCheck, ExternalLink, RotateCcw } from "lucide-react";
import { toast } from "sonner";
import { trackEvent, EVENTS } from "../lib/analytics";
import GoogleLinkButton from "../components/GoogleLinkButton";
import TelegramIcon from "../components/icons/TelegramIcon";
import { SOCIAL_CHANNELS, openChannel } from "../lib/social";

export default function Settings() {
  const { user, setUser, logout, refresh } = useAuth();
  const { effectivePremium, access, refresh: refreshAccess } = useAccess();
  const premium = usePremiumStatus();  // unified plan-state — see usePremiumStatus
  const { mode, setMode, palette, setPalette, palettes } = useTheme();
  const navigate = useNavigate();
  const [deleteOpen, setDeleteOpen] = useState(false);
  const [deleteConfirm, setDeleteConfirm] = useState("");
  const [deleting, setDeleting] = useState(false);
  const [resetOpen, setResetOpen] = useState(false);
  const [resetConfirm, setResetConfirm] = useState("");
  const [resetting, setResetting] = useState(false);

  // Local shadow of the server dismissal so the "Reset" button reflects the
  // action immediately without waiting for /me/access to re-hydrate.
  const [tgDismissedLocal, setTgDismissedLocal] = useState(null);
  const tgDismissed = tgDismissedLocal !== null
    ? tgDismissedLocal
    : !!access?.social_prefs?.[SOCIAL_CHANNELS.telegram.prefKey];

  const resetTelegramRecommend = async () => {
    setTgDismissedLocal(false);
    try {
      await api.post(SOCIAL_CHANNELS.telegram.resetEndpoint);
      toast.success("Telegram recommendation reset — it'll re-appear on Dashboard.");
      refreshAccess?.();
    } catch {
      setTgDismissedLocal(null); // revert
      toast.error("Couldn't reset — try again in a moment.");
    }
  };

  const doLogout = async () => {
    await logout();
    navigate("/");
  };

  const doDelete = async () => {
    if (deleting) return;
    setDeleting(true);
    try {
      await api.delete("/account");
      trackEvent(EVENTS.ACCOUNT_DELETED);
      // Backend cleared cookies AND user doc; mirror that on the client by
      // dropping the Bearer token from localStorage so no stale credential
      // gets attached to the next request.
      clearToken();
      setUser(null);
      toast.success("Your account has been deleted.");
      navigate("/", { replace: true });
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Couldn't delete account");
    } finally {
      setDeleting(false);
      setDeleteOpen(false);
      setDeleteConfirm("");
    }
  };

  const doResetProgress = async () => {
    if (resetting) return;
    setResetting(true);
    try {
      await api.post("/account/reset-study-progress");
      // Re-hydrate the user doc so streak/hours/badges read 0 everywhere
      // without requiring a manual page reload.
      await refresh?.();
      refreshAccess?.();
      toast.success("Study progress reset — you're starting fresh from zero.");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Couldn't reset progress — try again.");
    } finally {
      setResetting(false);
      setResetOpen(false);
      setResetConfirm("");
    }
  };

  const choosePalette = (p) => {
    if (p.premium && !effectivePremium) {
      toast.error("Premium themes are a Syllox Premium perk.");
      return;
    }
    setPalette(p.id);
    toast.success(`Theme: ${p.label}`);
  };

  return (
    <div className="space-y-6" data-testid="settings-root">
      <div>
        <div className="text-xs uppercase tracking-[0.2em] text-emerald-600">Settings</div>
        <h1 className="font-heading text-3xl font-bold tracking-tight">Settings</h1>
      </div>

      <div className="rounded-2xl border border-zinc-200 bg-white p-5 dark:border-zinc-800 dark:bg-zinc-900">
        <h3 className="font-heading text-lg font-semibold">Appearance</h3>
        <p className="text-sm text-zinc-500">Choose how Syllox looks on this device.</p>
        <div className="mt-4 grid grid-cols-3 gap-2">
          {[{ k: "light", icon: Sun, label: "Light" }, { k: "dark", icon: Moon, label: "Dark" }, { k: "system", icon: Monitor, label: "System" }].map(({ k, icon: Icon, label }) => (
            <button
              key={k}
              onClick={() => setMode(k)}
              data-testid={`settings-theme-${k}`}
              className={`flex flex-col items-center gap-1.5 rounded-xl border p-4 transition ${mode === k ? "border-emerald-500 bg-emerald-500/5" : "border-zinc-200 hover:border-zinc-300 dark:border-zinc-800 dark:hover:border-zinc-700"}`}
            >
              <Icon size={18} />
              <span className="text-xs font-medium">{label}</span>
            </button>
          ))}
        </div>
      </div>

      <div className="rounded-2xl border border-zinc-200 bg-white p-5 dark:border-zinc-800 dark:bg-zinc-900" data-testid="palette-card">
        <div className="flex items-start justify-between gap-2">
          <div>
            <h3 className="font-heading text-lg font-semibold">Premium Themes</h3>
            <p className="text-sm text-zinc-500">Match Syllox to your vibe. Minimal White is free for everyone — the rest are unlocked with Syllox Premium.</p>
          </div>
          {!effectivePremium && (
            <button
              onClick={() => navigate("/premium")}
              className="hidden shrink-0 rounded-full bg-gradient-to-r from-amber-400 to-orange-500 px-3 py-1.5 text-xs font-semibold text-white shadow-sm shadow-amber-500/30 sm:inline-flex"
              data-testid="palette-upgrade-cta"
            >
              <Crown size={12} className="mr-1 inline -mt-0.5"/> Unlock
            </button>
          )}
        </div>
        <div className="mt-4 grid grid-cols-2 gap-2 sm:grid-cols-3">
          {palettes.map((p) => {
            const active = palette === p.id;
            const locked = p.premium && !effectivePremium;
            return (
              <button
                key={p.id}
                onClick={() => choosePalette(p)}
                data-testid={`palette-${p.id}`}
                className={`group relative flex items-center gap-2.5 rounded-xl border p-3 text-left transition ${active ? "border-emerald-500 bg-emerald-500/5" : "border-zinc-200 hover:border-zinc-300 dark:border-zinc-800 dark:hover:border-zinc-700"} ${locked ? "opacity-70" : ""}`}
              >
                <span
                  className="h-7 w-7 shrink-0 rounded-full ring-2 ring-white shadow-sm dark:ring-zinc-900"
                  style={{ backgroundColor: p.hue }}
                />
                <div className="min-w-0 flex-1">
                  <div className="flex items-center gap-1.5 truncate text-sm font-medium">
                    <span className="truncate">{p.label}</span>
                    {p.premium && <Crown size={11} className="shrink-0 text-amber-500" />}
                  </div>
                  <div className="text-[10px] uppercase tracking-wider text-zinc-400">
                    {locked ? "Premium" : active ? "Active" : (p.premium ? "Included" : "Free")}
                  </div>
                </div>
                {locked && <Lock size={14} className="shrink-0 text-zinc-400"/>}
                {active && !locked && <Check size={14} className="shrink-0 text-emerald-500"/>}
              </button>
            );
          })}
        </div>
      </div>

      <div className="rounded-2xl border border-zinc-200 bg-white p-5 dark:border-zinc-800 dark:bg-zinc-900" data-testid="settings-community-card">
        <h3 className="font-heading text-lg font-semibold">Community</h3>
        <p className="text-sm text-zinc-500">Follow Syllox across your favourite channels.</p>

        <div className="mt-4 flex flex-col gap-4 rounded-xl border border-sky-500/25 bg-gradient-to-br from-sky-500/10 via-transparent to-transparent p-4 sm:flex-row sm:items-center dark:from-sky-500/10">
          <div className="flex items-center gap-3">
            <div className="grid h-10 w-10 shrink-0 place-items-center rounded-xl bg-gradient-to-br from-sky-500 to-cyan-500 text-white shadow-md shadow-sky-500/25">
              <TelegramIcon size={20} />
            </div>
            <div>
              <div className="font-heading text-sm font-semibold">Official Telegram Channel</div>
              <div className="text-xs text-zinc-500">
                {SOCIAL_CHANNELS.telegram.handle} · Notes, DPPs, PYQs, announcements
              </div>
            </div>
          </div>

          <div className="ml-auto flex flex-col items-stretch gap-2 sm:flex-row sm:items-center">
            <span
              data-testid="settings-telegram-status"
              className={`inline-flex items-center gap-1.5 rounded-full px-2.5 py-1 text-[11px] font-semibold ${
                tgDismissed
                  ? "bg-emerald-500/10 text-emerald-600 dark:text-emerald-400"
                  : "bg-zinc-500/10 text-zinc-500"
              }`}
            >
              <span className={`h-1.5 w-1.5 rounded-full ${tgDismissed ? "bg-emerald-500" : "bg-zinc-400"}`} />
              {tgDismissed ? "Interacted" : "Recommended"}
            </span>
            <Button
              onClick={() => openChannel("telegram")}
              data-testid="settings-telegram-join"
              className="bg-sky-500 text-white hover:bg-sky-600"
            >
              <TelegramIcon size={14} className="mr-1.5" /> Join Telegram
              <ExternalLink size={12} className="ml-1.5 opacity-70" />
            </Button>
          </div>
        </div>

        {tgDismissed && (
          <button
            type="button"
            onClick={resetTelegramRecommend}
            data-testid="settings-telegram-reset"
            className="mt-3 inline-flex items-center gap-1.5 text-xs font-medium text-zinc-500 transition hover:text-emerald-600 dark:hover:text-emerald-400"
          >
            <RotateCcw size={12} /> Reset Telegram recommendation
          </button>
        )}
      </div>

      <div className="rounded-2xl border border-zinc-200 bg-white p-5 dark:border-zinc-800 dark:bg-zinc-900">
        <h3 className="font-heading text-lg font-semibold">Account</h3>
        <div className="mt-3 text-sm text-zinc-600 dark:text-zinc-400">
          <div><span className="text-zinc-500">Name</span> · {user?.name}</div>
          <div><span className="text-zinc-500">{user?.email ? "Email" : "Phone"}</span> · {user?.email || (user?.phone ? `+91 ${user.phone}` : "—")}</div>
          <div><span className="text-zinc-500">Username</span> · @{user?.username}</div>
          <div data-testid="settings-plan-line">
            <span className="text-zinc-500">Plan</span> ·{" "}
            {premium.isPaid
              ? (premium.expiresAt
                  ? `Syllox Premium · Renews ${formatExpiryDate(premium.expiresAt)}`
                  : "Syllox Premium")
              : premium.isTrial
                ? `Welcome trial · ${premium.daysLeft ?? 0} day${premium.daysLeft === 1 ? "" : "s"} left`
                : "Free"}
          </div>
        </div>
        <div className="mt-4 flex gap-2">
          <Button variant="outline" onClick={() => navigate("/profile")} data-testid="settings-edit-profile">Edit profile</Button>
          <Button variant="destructive" onClick={doLogout} data-testid="settings-logout"><LogOut size={14} className="mr-1.5"/> Log out</Button>
        </div>
      </div>

      {/* Google sign-in migration card — visible only to users who signed up
          via phone-OTP and don't yet have an email attached. Phone-OTP is
          feature-flagged off as of Feb 2026; linking Google here ensures they
          can still log back in after their current JWT expires. */}
      {!user?.email && (
        <div
          className="rounded-2xl border border-emerald-500/30 bg-emerald-50/60 p-5 dark:border-emerald-500/30 dark:bg-emerald-500/5"
          data-testid="link-google-card"
        >
          <div className="flex items-start gap-3">
            <div className="grid h-9 w-9 shrink-0 place-items-center rounded-lg bg-emerald-500/15 text-emerald-700 dark:text-emerald-400">
              <ShieldCheck size={16}/>
            </div>
            <div className="min-w-0 flex-1">
              <h3 className="font-heading text-lg font-semibold text-emerald-700 dark:text-emerald-400">Add Google sign-in</h3>
              <p className="mt-1 text-sm text-zinc-600 dark:text-zinc-400">
                Syllox now uses Google sign-in only. Link your Google account so you can always log back in — your subjects, sessions, trial, premium, and all your data stay exactly the same.
              </p>
              <div className="mt-4">
                <GoogleLinkButton testId="settings-link-google-button" />
              </div>
            </div>
          </div>
        </div>
      )}

      <div className="rounded-2xl border border-amber-500/30 bg-amber-50/40 p-5 dark:border-amber-500/30 dark:bg-amber-500/5" data-testid="reset-progress-zone">
        <div className="flex items-start gap-3">
          <div className="grid h-9 w-9 shrink-0 place-items-center rounded-lg bg-amber-500/15 text-amber-600 dark:text-amber-400">
            <RotateCcw size={16}/>
          </div>
          <div className="flex-1">
            <h3 className="font-heading text-lg font-semibold text-amber-700 dark:text-amber-400">Reset study progress</h3>
            <p className="mt-1 text-sm text-zinc-600 dark:text-zinc-400">
              Start your academic journey from zero. This permanently clears your streak, study hours, session history, syllabus progress, targets, mock tests, badges, and leaderboard standing. Your account, profile, premium plan, and community posts are <span className="font-semibold">not affected</span>. This action is <span className="font-semibold">irreversible</span>.
            </p>
            <Button
              variant="outline"
              onClick={() => setResetOpen(true)}
              data-testid="settings-reset-progress-btn"
              className="mt-4 border-amber-500/50 text-amber-700 hover:bg-amber-500/10 hover:text-amber-800 dark:text-amber-400 dark:hover:bg-amber-500/10"
            >
              <RotateCcw size={14} className="mr-1.5"/> Reset study progress
            </Button>
          </div>
        </div>
      </div>

      <div className="rounded-2xl border border-red-500/30 bg-red-50/40 p-5 dark:border-red-500/30 dark:bg-red-500/5" data-testid="danger-zone">
        <div className="flex items-start gap-3">
          <div className="grid h-9 w-9 shrink-0 place-items-center rounded-lg bg-red-500/15 text-red-600 dark:text-red-400">
            <AlertTriangle size={16}/>
          </div>
          <div className="flex-1">
            <h3 className="font-heading text-lg font-semibold text-red-700 dark:text-red-400">Danger zone</h3>
            <p className="mt-1 text-sm text-zinc-600 dark:text-zinc-400">
              Permanently delete your account and all study data. This action is <span className="font-semibold">irreversible</span>.
            </p>
            <Button
              variant="outline"
              onClick={() => setDeleteOpen(true)}
              data-testid="settings-delete-account-btn"
              className="mt-4 border-red-500/50 text-red-600 hover:bg-red-500/10 hover:text-red-700 dark:hover:bg-red-500/10"
            >
              <Trash2 size={14} className="mr-1.5"/> Delete my account
            </Button>
          </div>
        </div>
      </div>

      <AlertDialog open={deleteOpen} onOpenChange={(o) => { setDeleteOpen(o); if (!o) setDeleteConfirm(""); }}>
        <AlertDialogContent className="max-w-md" data-testid="delete-account-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle className="flex items-center gap-2 text-red-600 dark:text-red-400">
              <AlertTriangle size={18}/> Delete account permanently?
            </AlertDialogTitle>
            <AlertDialogDescription>
              This will permanently erase your profile, syllabus, chapters, study sessions, targets, friends, posts, messages, and sessions. <span className="font-semibold text-red-600 dark:text-red-400">This cannot be undone.</span>
            </AlertDialogDescription>
          </AlertDialogHeader>
          <div className="mt-2 space-y-2">
            <label className="text-xs font-medium text-zinc-600 dark:text-zinc-400">
              Type <span className="rounded bg-zinc-100 px-1.5 py-0.5 font-mono text-zinc-900 dark:bg-zinc-800 dark:text-zinc-100">DELETE</span> to confirm
            </label>
            <Input
              value={deleteConfirm}
              onChange={(e) => setDeleteConfirm(e.target.value)}
              placeholder="DELETE"
              autoComplete="off"
              data-testid="delete-account-confirm-input"
            />
          </div>
          <AlertDialogFooter className="mt-2">
            <AlertDialogCancel data-testid="delete-account-cancel">Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={(e) => { e.preventDefault(); doDelete(); }}
              disabled={deleteConfirm !== "DELETE" || deleting}
              className="bg-red-600 text-white hover:bg-red-700"
              data-testid="delete-account-confirm-btn"
            >
              {deleting ? "Deleting…" : "Yes, delete my account"}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <AlertDialog open={resetOpen} onOpenChange={(o) => { setResetOpen(o); if (!o) setResetConfirm(""); }}>
        <AlertDialogContent className="max-w-md" data-testid="reset-progress-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle className="flex items-center gap-2 text-amber-600 dark:text-amber-400">
              <RotateCcw size={18}/> Reset study progress?
            </AlertDialogTitle>
            <AlertDialogDescription>
              This will permanently clear your streak, total study hours, session history, syllabus and chapter progress, targets, mock tests, study badges, and leaderboard standing. Your account, profile, premium plan, trial, payments, and community activity stay untouched. <span className="font-semibold text-amber-600 dark:text-amber-400">This cannot be undone.</span>
            </AlertDialogDescription>
          </AlertDialogHeader>
          <div className="mt-2 space-y-2">
            <label className="text-xs font-medium text-zinc-600 dark:text-zinc-400">
              Type <span className="rounded bg-zinc-100 px-1.5 py-0.5 font-mono text-zinc-900 dark:bg-zinc-800 dark:text-zinc-100">RESET</span> to confirm
            </label>
            <Input
              value={resetConfirm}
              onChange={(e) => setResetConfirm(e.target.value)}
              placeholder="RESET"
              autoComplete="off"
              data-testid="reset-progress-confirm-input"
            />
          </div>
          <AlertDialogFooter className="mt-2">
            <AlertDialogCancel data-testid="reset-progress-cancel">Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={(e) => { e.preventDefault(); doResetProgress(); }}
              disabled={resetConfirm !== "RESET" || resetting}
              className="bg-amber-600 text-white hover:bg-amber-700"
              data-testid="reset-progress-confirm-btn"
            >
              {resetting ? "Resetting…" : "Yes, reset my progress"}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
